import { gql } from '@apollo/client';

export const BASIC_USER_FRAGMENT = gql`
  fragment BasicUserFragment on User {
    id
    firstName
    lastName
    role
    email
    tenantId
    clientId
    profilePictureUrl
    profileSummary
    onboardingPercentage
    cognitoCreatedAt
    updatedAt
    address {
      addressLine1
      addressLine2
      suburb
      state
      postcode
      countryLabel
      countryValue
    }
    latLng {
      lat
      lng
    }
    idealRadius
    assignedAdminId
    cognitoSub
    addressHash
    preferredLanguage
    status
    dob
    phoneNumber
    siteIds
    classificationIds
    classificationLevelIds
    classificationLevels {
      id
      name
      classificationId
    }
    employmentDetails {
      employeeId
      employeeNumber
      payrollId
      department
      workLocation
      costCenter
      startDate
      employmentType
      payGroup
      position
    }
  }
`;

export const USER_FRAGMENT = gql`
  fragment UserFragment on User {
    ...BasicUserFragment
    customAttributes {
      ref
      value
      valueType
    }
  }
  ${BASIC_USER_FRAGMENT}
`;

export const DETAILED_USER_FRAGMENT = gql`
  fragment DetailedUserFragment on User {
    ...UserFragment
    vevoCheck {
      id
      userId
      tenantId
      vevoCheckWorkConditionCode
      vevoCheckVisaSubClassCode
      vevoCheckWorkEntitlementStatus
      vevoCheckVisaExpiry
      maxHourLimit
      createdAt
      updatedAt
      expiresAt
      passportNumber
      passportIssueCountry
      residencyStatus
    }
  }
  ${USER_FRAGMENT}
`;

export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

export const GET_USER_DETAILED = gql`
  query GetUser($id: String!) {
    getUser(id: $id) {
      ...DetailedUserFragment
    }
  }
  ${DETAILED_USER_FRAGMENT}
`;

export const GET_USER = gql`
  query GetUser($id: String!) {
    getUser(id: $id) {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

export const GET_USERS_BY_TENANT = gql`
  query GetUsersByTenant($tenantId: String!, $roles: [UserRole!]) {
    getUsersByTenantId(tenantId: $tenantId, roles: $roles) {
      ...BasicUserFragment
    }
  }
  ${BASIC_USER_FRAGMENT}
`;

// Lightweight fragment containing only the fields rendered/filtered/sorted in
// the Workers list view. Keep this in sync with `useWorkerColumns`,
// `workerFilters.utils.ts`, and the search/sort logic in `Workers.tsx`.
// Detail views use `fetchDetailedWorkerById` which returns the full user.
export const WORKER_LIST_FRAGMENT = gql`
  fragment WorkerListFragment on User {
    id
    firstName
    lastName
    email
    phoneNumber
    profilePictureUrl
    status
    onboardingPercentage
    cognitoCreatedAt
    updatedAt
    assignedAdminId
    classificationLevelIds
    address {
      state
      suburb
      postcode
    }
    # TODO: Uncomment this when we have a way to fetch specific custom attributes
    # customAttributes {
    #   ref
    #   value
    # }
    employmentDetails {
      startDate
    }
  }
`;

export const GET_WORKERS_LIST_BY_TENANT = gql`
  query GetWorkersListByTenant($tenantId: String!, $roles: [UserRole!]) {
    getUsersByTenantId(tenantId: $tenantId, roles: $roles) {
      ...WorkerListFragment
    }
  }
  ${WORKER_LIST_FRAGMENT}
`;

// Get users by client id. This fetches all CLIENT_ADMIN and CLIENT_USER roles.
export const GET_USERS_BY_CLIENT = gql`
  query GetUsersByClientId($clientId: String!) {
    getUsersByClientId(clientId: $clientId) {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

// Get users who have worked for a client. This fetches all WORKER roles.
export const GET_WORKERS_BY_CLIENT = gql`
  query GetWorkersByClientId($clientId: String!) {
    getWorkersByClientId(clientId: $clientId) {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

// User Mutations

export const UPDATE_USER = gql`
  mutation UpdateUser($id: String, $details: UserInput!) {
    updateUser(id: $id, details: $details) {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

export const UPDATE_USER_STATUS = gql`
  mutation UpdateUserStatus($id: String!, $status: UserStatus!) {
    updateUserStatus(id: $id, status: $status) {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

export const UPDATE_USER_ASSIGNED_ADMIN = gql`
  mutation UpdateUserAssignedAdmin(
    $id: String!
    $assignedAdminId: String!
    $changeReason: String
  ) {
    updateUserAssignedAdmin(
      id: $id
      assignedAdminId: $assignedAdminId
      changeReason: $changeReason
    ) {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: String!) {
    deleteUser(id: $id)
  }
`;

export const UPDATE_USER_FCM_TOKEN = gql`
  mutation UpdateUserFCMToken($fcmToken: String!) {
    updateUserFCMToken(fcmToken: $fcmToken)
  }
`;

export const GET_STATS_FOR_CURRENT_USER = gql`
  query GetStatsForCurrentUser {
    getStatsForCurrentUser {
      earningsForCurrentWeek: earningsForPeriod
      allUpcomingEarnings: upcomingEarningsForPeriod
      streak
    }
  }
`;

export const ASSIGN_CLASSIFICATION_LEVEL_TO_USER = gql`
  mutation AssignClassificationLevelToUser(
    $userId: String!
    $classificationLevelId: String!
  ) {
    assignClassificationLevelToUser(
      userId: $userId
      classificationLevelId: $classificationLevelId
    ) {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

export const REMOVE_CLASSIFICATION_LEVEL_FROM_USER = gql`
  mutation RemoveClassificationLevelFromUser(
    $userId: String!
    $classificationLevelId: String!
  ) {
    removeClassificationLevelFromUser(
      userId: $userId
      classificationLevelId: $classificationLevelId
    ) {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

export const INITIATE_USER_SELF_DELETE = gql`
  mutation InitiateUserSelfDeletion($reason: String) {
    initiateUserSelfDeletion(reason: $reason)
  }
`;
